import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Params } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';

// A single internal link rendered in the shell body.
export interface TaxonomyShellLink {
  label: string;
  route: string;
  description?: string;
}

// A breadcrumb crumb. The last crumb is rendered as plain text (current page).
export interface TaxonomyShellCrumb {
  label: string;
  route?: string;
}

// Data-driven content for a taxonomy shell page. Supplied entirely via the
// route's `data.shell` block in app.module.ts so a single component can back
// every Phase 2B taxonomy page without bespoke logic. The listing area is a
// placeholder for now — real plate data is wired in a later phase.
//
// Any visible string (heading, intro, link labels/descriptions, breadcrumb
// labels, listing placeholder) may contain route-param tokens:
//   {KEY}  -> the route param `key`, UPPERCASED for display
//   {key}  -> the route param `key`, used verbatim (kept lowercase like the URL)
// e.g. for /plates/initials/:initials, heading "{INITIALS} Number Plates"
// renders "BF Number Plates" while the URL/canonical stay /plates/initials/bf.
export interface TaxonomyShellContent {
  heading: string;
  intro?: string[];
  links?: TaxonomyShellLink[];
  linksHeading?: string;
  showViewerCta?: boolean;
  showValuationCta?: boolean;
  breadcrumbs?: TaxonomyShellCrumb[];
  listingPlaceholder?: string;
}

@Component({
  selector: 'pla-taxonomy-shell',
  templateUrl: './taxonomy-shell.component.html',
  styleUrls: ['./taxonomy-shell.component.scss'],
})
export class TaxonomyShellComponent implements OnInit, OnDestroy {
  public content: TaxonomyShellContent | null = null;
  private sub: Subscription | null = null;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    // Combine data + params (not just snapshot) so navigating between two shell
    // routes that reuse this component instance — e.g. /plates/dateless →
    // /plates/prefix, or /plates/initials/bf → /plates/initials/cd — refreshes
    // both the content and the interpolated param values.
    this.sub = combineLatest([this.route.data, this.route.params]).subscribe(
      ([data, params]: [Data, Params]) => {
        const raw = data['shell'] as TaxonomyShellContent | undefined;
        this.content = raw ? this.interpolateContent(raw, params) : null;
      }
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  // Produce a copy of the shell content with every visible string interpolated
  // against the current route params. Non-string/structural fields are passed
  // through untouched.
  private interpolateContent(
    content: TaxonomyShellContent,
    params: Params
  ): TaxonomyShellContent {
    return {
      ...content,
      heading: this.interpolate(content.heading, params),
      intro: content.intro?.map((p) => this.interpolate(p, params)),
      linksHeading: content.linksHeading
        ? this.interpolate(content.linksHeading, params)
        : content.linksHeading,
      listingPlaceholder: content.listingPlaceholder
        ? this.interpolate(content.listingPlaceholder, params)
        : content.listingPlaceholder,
      links: content.links?.map((l) => ({
        ...l,
        label: this.interpolate(l.label, params),
        description: l.description
          ? this.interpolate(l.description, params)
          : l.description,
      })),
      breadcrumbs: content.breadcrumbs?.map((c) => ({
        ...c,
        label: this.interpolate(c.label, params),
      })),
    };
  }

  // Replace {key} / {KEY} tokens with the matching route param. An UPPERCASE
  // token uppercases the value (for display); a lowercase token leaves it as-is
  // (matching the lowercase URL segment). Unknown tokens are left untouched.
  private interpolate(text: string, params: Params): string {
    if (!text) {
      return text;
    }
    return text.replace(/\{([a-zA-Z0-9_]+)\}/g, (match: string, token: string) => {
      const value = params[token.toLowerCase()];
      if (value == null) {
        return match;
      }
      return token === token.toUpperCase()
        ? String(value).toUpperCase()
        : String(value);
    });
  }
}
